import { makeAutoObservable } from 'mobx';

import { SetupPresetFilterStructure } from '@/media/entity/SetupPresetFilterStructure';
import { isDiamond, ProductType } from '@/product/constants/productTypes';

import SetupPreset, {
  I_MUNSELL,
  I_MUNSELL_CLOSEUP,
  NON_DIAMOND_SETUP_PRESETS,
  SetupPresetModel,
} from '../entity/SetupPreset';

export const BACKGROUND_COLOR_DEFAULT = '#000000';
export const BACKGROUND_COLOR_WHITE = '#ffffff';
export const BACKGROUND_COLOR_AUTO = 'auto';

export default class SetupPresetRepository {
  setupPresets: SetupPreset[] = [];

  setupPresetOrder: number[] = [];

  setupPresetsFilterStructure: SetupPresetFilterStructure = {};

  constructor() {
    makeAutoObservable(this);
  }

  get nonDiamondSetupPresets() {
    const setupSet = new Set(NON_DIAMOND_SETUP_PRESETS);

    return this.setupPresets.filter((sp) => setupSet.has(sp.id));
  }

  get defaultSetupPreset() {
    if (!this.setupPresets || this.setupPresets.length === 0) {
      return null;
    }

    const spOrder = this.getSetupPresetOrder();

    for (const spId of spOrder) {
      const sp = this.findSetupPresetById(spId);

      if (sp) {
        return sp;
      }
    }

    return null;
  }

  initSetupPresets(setupPresets: SetupPresetModel[]) {
    this.setupPresets = setupPresets.map((setupPresetsItem) => new SetupPreset(setupPresetsItem, true));
  }

  initSetupPresetOrder(setupPresetOrderData: number[]) {
    this.setupPresetOrder = setupPresetOrderData;
  }

  initSetupPresetsFilteringStructure(setupPresetsFilterStructure: SetupPresetFilterStructure) {
    this.setupPresetsFilterStructure = setupPresetsFilterStructure;
  }

  findSetupPresetById(id: Nullable<number>) {
    if (!this.setupPresets || this.setupPresets.length === 0) {
      return null;
    }

    return this.setupPresets.find((sp) => sp.id === id) ?? null;
  }

  findSetupPresetBackground(setupPreset: Nullable<SetupPreset>) {
    if (!setupPreset) {
      return BACKGROUND_COLOR_DEFAULT;
    }

    if (setupPreset.id === I_MUNSELL || setupPreset.id === I_MUNSELL_CLOSEUP) {
      return BACKGROUND_COLOR_WHITE;
    }

    const bgColor = setupPreset.bg;

    if (!bgColor) {
      return BACKGROUND_COLOR_DEFAULT;
    }

    if (bgColor === BACKGROUND_COLOR_AUTO) {
      return null;
    }

    return bgColor;
  }

  getSetupPresetOrder() {
    if (!this.setupPresetOrder) {
      return [];
    }

    return this.setupPresetOrder;
  }

  getSetupPresetsByProductType(productType: ProductType): SetupPreset[] {
    return isDiamond(productType) ? this.setupPresets : this.nonDiamondSetupPresets;
  }
}

export const setupPresetRepository = new SetupPresetRepository();
